import { useTxGuard } from '@cronos-app/components';
import { getRelatedCronosEvmChain } from '@cronos-app/sdk';
import { currentWallet } from '@cronos-app/wallet';
import { useToast } from '@ui/hooks/useToast';
import {
  revalidateCdcDepositAddress,
  useCdcCardView,
  useCdcDepositAddress,
  useCdcDepositCardService,
} from '@ui/views/card-cronos/core/utils';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';

import { CardView } from '../../core/types';

export enum CardStatus {
  None,
  Unbond,
  Bond,
}

export const useCardAction = () => {
  const account = currentWallet.useAccount();
  const network = currentWallet.useDesiredNetwork();
  const depositAddress = useCdcDepositAddress();
  const service = useCdcDepositCardService();
  const { setView } = useCdcCardView();
  const toast = useToast();
  const t = useTranslations();
  const bindCardWithGuard = useTxGuard<[string], undefined>(
    useCallback(
      async ({ provider }, depositAddress) => {
        try {
          await service.bindCard(await provider.getSigner(), depositAddress);
          revalidateCdcDepositAddress();
        } catch (e) {
          toast({
            status: 'error',
            title: t('card.Transaction Failed!'),
            description:
              e instanceof Error &&
              'reason' in e &&
              typeof e.reason === 'string'
                ? e.reason
                : t('card.Something went wrong, please try again!'),
            duration: null,
          });
          throw e;
        }
      },
      [service, t, toast],
    ),
    getRelatedCronosEvmChain(network),
  );
  const unbindCardWithGuard = useTxGuard(
    useCallback(
      async ({ provider }) => {
        try {
          await service.unbindCard(await provider.getSigner());
          revalidateCdcDepositAddress();
          setView(CardView.First);
        } catch (e) {
          toast({
            status: 'error',
            title: t('card.Transaction Failed!'),
            description:
              e instanceof Error &&
              'reason' in e &&
              typeof e.reason === 'string'
                ? e.reason
                : t('card.Something went wrong, please try again!'),
            duration: null,
          });
          throw e;
        }
      },
      [service, setView, t, toast],
    ),
    getRelatedCronosEvmChain(network),
  );
  return {
    bindCard: bindCardWithGuard,
    unbindCard: unbindCardWithGuard,
    status: account
      ? depositAddress.data
        ? CardStatus.Bond
        : CardStatus.Unbond
      : CardStatus.None,
  };
};
